/**
 * カードの型（docs/spec/card-schema.md §5）。schemas/card.schema.json と 1 対 1 に手で書く。
 * examples/cards の全件を読み込むテストで schema との一致を保つ。
 */
import type { MeterSpec } from '../complexity';
import type { VizState } from '../viz';

export type { MeterSpec, VizState };

export type CardType =
  | 'predict-output'
  | 'fill-blank'
  | 'parsons'
  | 'choice'
  | 'reduce'
  | 'complexity-quiz'
  | 'write-ac'
  | 'viz-step'
  | 'memo';

export const CARD_TYPES: readonly CardType[] = [
  'predict-output',
  'fill-blank',
  'parsons',
  'choice',
  'reduce',
  'complexity-quiz',
  'write-ac',
  'viz-step',
  'memo',
];

export type Technique =
  | 'single-loop'
  | 'double-loop-search'
  | 'triple-loop-search'
  | 'digit-while'
  | 'array-count'
  | 'dict'
  | 'set'
  | 'stack'
  | 'queue'
  | 'cumulative-sum'
  | 'sort-and-binary-search'
  | 'sort-and-greedy'
  | 'two-pointers'
  | 'bit-full-search'
  | 'bfs'
  | 'dfs'
  | 'dp';

export type Grading = 'client' | 'server' | 'client-then-server';

export interface CardBase {
  id: string;
  type: CardType;
  grading: Grading;
  unit: string;
  stage: number;
  title: string;
  useCase?: string;
  textbook: string;
  difficulty: 1 | 2 | 3;
  tags: string[];
  hints?: string[];
  explanation: string;
  formerIds?: string[];
  draft?: boolean;
  /** ユニット内の出題順。小さいほど先。省略は {@link DEFAULT_CARD_ORDER}（card-schema.md §1）。 */
  order?: number;
}

/** `order` を省略したカードの順位。 */
export const DEFAULT_CARD_ORDER = 1000;

export interface ChoiceOption {
  text: string;
  correct: boolean;
  accepted?: boolean;
  explanation?: string;
}

export interface Constraint {
  name: string;
  min?: number;
  max: number;
}

export type PredictOutcome = 'ok' | 'compile-error' | 'runtime-error';

export interface PredictOutputCard extends CardBase {
  type: 'predict-output';
  grading: 'client';
  code: string;
  stdin?: string;
  outcome?: PredictOutcome;
  expected?: string;
  choices?: string[];
}

export interface Blank {
  id: number;
  accept: string[];
  match?: 'trim' | 'ignore-spaces';
  width?: number;
}

export interface FillBlankCard extends CardBase {
  type: 'fill-blank';
  grading: 'client' | 'client-then-server';
  template: string;
  blanks: Blank[];
  stdin?: string;
  expected?: string;
}

export interface ParsonsCard extends CardBase {
  type: 'parsons';
  grading: 'client';
  lines: string[];
  distractors?: string[];
  alternatives?: number[][];
  stdin?: string;
  expected?: string;
}

export interface ChoiceCard extends CardBase {
  type: 'choice';
  grading: 'client';
  question: string;
  code?: string;
  choices: ChoiceOption[];
  multiple?: boolean;
}

export interface ReduceOption {
  technique: Technique;
  note?: string;
  correct: boolean;
  accepted?: boolean;
  explanation?: string;
}

export interface ReduceCard extends CardBase {
  type: 'reduce';
  grading: 'client';
  problem: string;
  constraints?: Constraint[];
  choices: ReduceOption[];
  reason: string;
  ops?: string;
}

export interface ComplexityQuizCard extends Omit<ChoiceCard, 'type'> {
  type: 'complexity-quiz';
  constraints?: Constraint[];
  ops?: string;
}

export interface WriteAcCase {
  name?: string;
  in: string;
  out: string;
}

export interface WriteAcCard extends CardBase {
  type: 'write-ac';
  grading: 'server';
  statement: string;
  starter: string;
  solution: string;
  cases: WriteAcCase[];
  source?: { kind: 'swift-classroom'; path: string };
  preflight?: { question: string; choices: string[]; answer: number };
  meter?: MeterSpec;
}

export interface VizStepFrame {
  state: Record<string, VizState>;
  note?: string;
  ticks?: number;
}

export interface VizCheckpoint {
  frame: number;
  prompt: string;
  choices: string[];
  answer: number;
}

export interface VizStepCard extends CardBase {
  type: 'viz-step';
  grading: 'client';
  code?: string;
  stdin?: string;
  frames: VizStepFrame[];
  checkpoints: VizCheckpoint[];
}

/** 想起問題（card-schema.md §3.9）。accept があれば穴埋め想起、無ければ自己申告。 */
export interface MemoRecall {
  question: string;
  answer: string;
  accept?: string[];
  match?: 'trim' | 'ignore-spaces';
}

/**
 * 暗記カード（card-schema.md §3.9）。textbook は任意、explanation と hints は持たない
 * （解説は why、答えは recall.answer）。
 */
export interface MemoCard extends Omit<CardBase, 'textbook' | 'explanation' | 'hints'> {
  type: 'memo';
  grading: 'client';
  textbook?: string;
  statement: string;
  snippet?: string;
  stdin?: string;
  expected?: string;
  why?: string;
  recall: MemoRecall;
}

export type Card =
  | PredictOutputCard
  | FillBlankCard
  | ParsonsCard
  | ChoiceCard
  | ReduceCard
  | ComplexityQuizCard
  | WriteAcCard
  | VizStepCard
  | MemoCard;

/** memo 以外のカード（textbook と explanation を必ず持つ）。 */
export type DrillCard = Exclude<Card, MemoCard>;

export function isMemoCard(card: Card): card is MemoCard {
  return card.type === 'memo';
}

/** カードファイル（card-schema.md §1）。 */
export interface CardFile {
  schemaVersion: 1;
  cards: Card[];
}

/** useCase 既定値ファイル（card-schema.md §2.3）。 */
export interface UseCasesFile {
  schemaVersion: 1;
  kind: 'use-cases';
  textbooks: Record<string, string>;
  units: Record<string, string>;
}

export type ContentFile = CardFile | UseCasesFile;
